import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Auth0Client, User } from '@auth0/auth0-spa-js';
import { useNavigate } from 'react-router';
import { AuthContext } from './authContext';
import {
  getErrorDescriptionParam,
  hasAuthErrorParams,
  hasAuthSuccessParams,
  deleteAuthSuccessParams,
} from './search-params';
import { initialAuthState } from './authState';

/**
 * 認証の redirect を跨いで情報を参照するための Auth0 AppState
 */
export type LoginWithRedirectAppState = {
  /**
   * redirect 後に戻り先ページを指定する
   */
  returnTo?: `/${string}`;

  /**
   * client 個別の処理を行うために引き回す
   */
  callbackClientId?: string;
};

export function AuthProvider({ client, children }: { client: Auth0Client; children: ReactNode }) {
  const [error, setError] = useState<Error | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | undefined>();
  const initialized = useRef(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;
    (async (): Promise<void> => {
      try {
        let _user: User | undefined;
        if (hasAuthSuccessParams()) {
          const { appState } = await client.handleRedirectCallback<LoginWithRedirectAppState>();
          _user = await client.getUser();

          if (appState?.returnTo && appState.returnTo.startsWith('/')) {
            await navigate(`${appState.returnTo}`);
            return;
          }
          deleteAuthSuccessParams();
        } else if (hasAuthErrorParams()) {
          setIsAuthenticated(false);
          const errorDescription = getErrorDescriptionParam();
          await navigate(`/unauthorized?error_description=${errorDescription}`);
          return;
        } else {
          await client.checkSession();
          _user = await client.getUser();
        }
        setIsAuthenticated(!!_user);
        setUser(_user);
      } catch (e: unknown) {
        if (e instanceof Error) {
          setError(e);
        }
      }
    })();
  }, [client, navigate]);

  /**
   * 明示的にログイン後のページを指定する場合は、 AuthProvider の範囲外 (loader / イベントハンドラ) で AppState を指定してログインすること
   * @see requireSignedIn
   */
  const login = useCallback(async () => {
    await client.loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}/home`,
      },
    });
  }, [client]);

  const logout = useCallback(async () => {
    await client.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [client]);

  return (
    <AuthContext.Provider value={{ ...initialAuthState, error, isAuthenticated, login, logout, user }}>
      {children}
    </AuthContext.Provider>
  );
}
